import type { ArticleFilters } from '~/models/articles'

const FILTER_KEYS = ['tags_id', 'rubric_id', 'audience_id', 'search'] as const
const DEFAULT_AUDIENCE = 'Все'

export async function useArticleFilters() {
    const route = useRoute()
    const router = useRouter()
    const { $services } = useNuxtApp()

    const searchValue = ref(String(route.query.search ?? ''))
    const activeAudience = ref(DEFAULT_AUDIENCE)

    const hasActiveFilters = computed(() => FILTER_KEYS.some(key => Boolean(route.query[key])))

    const audienceTabs = computed<string[]>(() => {
        const options = filters.value?.audiences.options ?? []
        return options.length ? [DEFAULT_AUDIENCE, ...options.map(o => o.name)] : [DEFAULT_AUDIENCE]
    })

    function buildFilterParams(): Record<string, string> {
        const params: Record<string, string> = {}
        for (const key of FILTER_KEYS) {
            const value = route.query[key]
            if (value) params[`filter[${key}]`] = String(value)
        }
        return params
    }

    function syncFromRoute() {
        searchValue.value = String(route.query.search ?? '')
        const audienceId = route.query.audience_id
        if (!audienceId) { activeAudience.value = DEFAULT_AUDIENCE; return }
        const match = filters.value?.audiences.options.find(o => String(o.id) === String(audienceId))
        activeAudience.value = match?.name ?? DEFAULT_AUDIENCE
    }

    const { data: filters, refresh } = await useAsyncData<ArticleFilters | null>(
        'article-filters',
        () => $services.articleService.main.getFilters(buildFilterParams()).catch(() => null),
    )

    syncFromRoute()

    async function loadFilters() {
        await refresh()
        syncFromRoute()
    }

    function navigate(query: Record<string, string>) {
        void router.push({ path: '/news', query })
    }

    function onChangeFilters(updated: Record<string, string | undefined>) {
        const merged = { ...route.query, ...updated }
        const query = Object.fromEntries(
            Object.entries(merged).filter(([k, v]) => k !== 'p' && v != null && v !== ''),
        ) as Record<string, string>
        navigate(query)
    }

    function handleAudienceChange(audience: string) {
        activeAudience.value = audience
        searchValue.value = ''

        const query = Object.fromEntries(
            Object.entries(route.query).filter(([k]) => k !== 'p' && k !== 'search' && k !== 'audience_id'),
        ) as Record<string, string>

        if (audience !== DEFAULT_AUDIENCE) {
            const match = filters.value?.audiences.options.find(o => o.name === audience)
            if (match) query.audience_id = String(match.id)
        }
        navigate(query)
    }

    function handleSearch(query: string) {
        activeAudience.value = DEFAULT_AUDIENCE
        navigate(query.trim() ? { search: query.trim() } : {})
    }

    function clearFilters() {
        activeAudience.value = DEFAULT_AUDIENCE
        searchValue.value = ''
        void router.push({ path: '/news' })
    }

    function viewAllCategory(categoryId: number) {
        activeAudience.value = DEFAULT_AUDIENCE
        searchValue.value = ''
        navigate({ rubric_id: String(categoryId) })
    }

    return {
        filters,
        activeAudience,
        searchValue,
        hasActiveFilters,
        audienceTabs,
        buildFilterParams,
        loadFilters,
        onChangeFilters,
        handleAudienceChange,
        handleSearch,
        clearFilters,
        viewAllCategory,
    }
}
